import NavDrawer from './NavDrawer';
import { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';
import { loadPublishedSets } from './setSource';
import FeedbackWidget from './FeedbackWidget';

// --- NEW badge helper (suggestion #3) ---
// A set is "new" if it has a publishedAt date within the last 30 days.
// Expected format: 'YYYY-MM-DD' (ISO date string).
const NEW_BADGE_DAYS = 30;
function isNewSet(set) {
  if (!set || !set.publishedAt) return false;
  const published = new Date(set.publishedAt);
  if (isNaN(published.getTime())) return false;
  const ageMs = Date.now() - published.getTime();
  const maxAgeMs = NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;
  return ageMs >= 0 && ageMs <= maxAgeMs;
}

// SECTIONS now lives in ./setSections so the Community Checklists page sees
// the exact same product-line categories — preventing the drift bug where a
// set could appear in one browse page but not the other.
import { SECTIONS, byNewest } from './setSections';
import { lightAccent } from './theme';
import { markSetOpenSource, trackEmptyStateViewed, trackEmptyStateSetClicked } from './activityTracker';
import { useFeatureFlag } from './featureFlags';

const OPEN_SECTIONS_KEY = 'ck_open_sections';

// Product-type filter pills. Values match the `type` field in setRegistry.js;
// sets without one (e.g. a Supabase-only set) fall under 'Other'.
const SET_TYPES = ['Galaxy', 'Chrome', 'Sapphire', 'Black', 'Hyperspace', 'Signature', 'Masterwork', 'Other'];
const setType = (set) => set.type || 'Other';

// The three sets a brand-new collector (zero collection rows) is offered
// instead of the full accordion. Rotate the first slot when a new set ships.
const FIRST_SET_PICKS = [
  { id: '2026-galaxy', tagline: 'NEWEST' },
  { id: '2025-chrome-galaxy', tagline: 'MOST COLLECTED' },
  { id: '2025-smugglers-outpost', tagline: 'BIG & VARIED' },
];

function defaultOpenSections() {
  try {
    const starred = JSON.parse(localStorage.getItem('ck_starred_sets') || '[]');
    const allClosed = SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: false }), { starred: false });
    if (starred.length > 0) return { ...allClosed, starred: true };
    return allClosed;
  } catch {
    return SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: false }), { starred: true });
  }
}

export default function SetBrowser({ user, profile, onSignOut, onEditProfile }) {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  // CK 3.0 empty state: total collection rows of ANY status. null = unknown
  // (still loading, or the query failed) — null NEVER shows the empty state,
  // so a failed count falls back to the accordion, never a blank screen.
  const [collectionRowCount, setCollectionRowCount] = useState(null);
  const [browseAll, setBrowseAll] = useState(false);
  const emptyStateEnabled = useFeatureFlag('empty_state_v2');
  const [setList, setSetList] = useState(SET_REGISTRY);
  const [starredSets, setStarredSets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ck_starred_sets') || '[]'); } catch { return []; }
  });
  const toggleStar = (setId, e) => {
    e.stopPropagation();
    setStarredSets(prev => {
      const next = prev.includes(setId) ? prev.filter(id => id !== setId) : [...prev, setId];
      localStorage.setItem('ck_starred_sets', JSON.stringify(next));
      return next;
    });
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [cardResults, setCardResults] = useState(null);
  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setCardResults(null); return; }
    const query = q.toLowerCase().trim();
    const results = [];
    await Promise.all(SET_REGISTRY.map(async (set) => {
      try {
        const module = await import('./sets/' + set.dataFile + '.js');
        (module.SETS || []).forEach(s => {
          const matches = s.cards.filter(c =>
            c.name.toLowerCase().includes(query) ||
            (c.artist || '').toLowerCase().includes(query) ||
            String(c.n).toLowerCase().includes(query)
          );
          if (matches.length > 0) results.push({ set, subSet: s, cards: matches });
        });
      } catch(e) {}
    }));
    setCardResults(results);
  };
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(OPEN_SECTIONS_KEY) || 'null');
      if (saved && typeof saved === 'object') return { ...defaultOpenSections(), ...saved };
    } catch {}
    return defaultOpenSections();
  });

  useEffect(() => {
    let mounted = true;
    loadPublishedSets().then(sets => {
      if (mounted) setSetList(sets);
    }).catch(e => {
      console.warn('[SetBrowser] Set load failed:', e);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      if (!user) { setLoading(false); return; }
      const { data } = await supabase
        .from('collections')
        .select('card_key, status')
        .eq('user_id', user.id);

      if (data) {
        setCollectionRowCount(data.length);
        const statsMap = {};
        await Promise.all(setList.map(async set => {
          const prefix = set.id + '__';
          const setCards = data.filter(r => r.card_key.startsWith(prefix) && !r.card_key.includes('__parallel__'));
          let total = set.totalCards;
          try {
            const module = await import(`./sets/${set.dataFile}.js`);
            const countableSets = (module.SETS || []).filter(s => s.type !== 'sketch');
            total = countableSets.reduce((sum, s) => sum + s.cards.length, 0);
          } catch(e) {}
          statsMap[set.id] = {
            collected: setCards.filter(r => r.status === 'collected').length,
            total,
          };
        }));
        setStats(statsMap);
      }
      setLoading(false);
    };
    loadStats();
  }, [user, setList]);

  const toggleSection = (id) => {
    setOpenSections(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Every checklist opened from this screen counts as source 'my_checklists'
  // in the set_opened activation event.
  const openSet = (setId) => {
    markSetOpenSource(setId, 'my_checklists');
    navigate(`/sets/${setId}`);
  };

  // --- CK 3.0 first-set empty state -------------------------------------
  // Shown instead of the whole browse UI when the flag is on and the user has
  // zero collection rows of any status. One card marked → the normal view
  // returns on next load (the condition simply re-evaluates).
  const showEmptyState = emptyStateEnabled && collectionRowCount === 0 && !browseAll && !loading;

  useEffect(() => {
    if (showEmptyState) trackEmptyStateViewed(user?.id); // once per session (tracker guards)
  }, [showEmptyState, user?.id]);

  const openFirstPick = (setId) => {
    trackEmptyStateSetClicked(user?.id, setId);
    markSetOpenSource(setId, 'empty_state');
    navigate(`/sets/${setId}`);
  };

  // Same tile chrome as renderSetCard (styles.card etc.), minus the star and
  // progress numbers a zero-card collector doesn't have yet.
  const renderFirstPickCard = (set, tagline) => {
    const accent = lightAccent(set.color);
    return (
      <div
        key={set.id}
        style={{ ...styles.card, borderLeft: '3px solid ' + accent }}
        onClick={() => openFirstPick(set.id)}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = accent;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 8px 26px ${accent}22`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.borderLeftColor = accent;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={styles.cardMeta}>
          <span>{set.year}</span>
          <span style={styles.cardMetaDot}>·</span>
          <span>{(set.brand || '').toUpperCase()}</span>
        </div>
        <div style={styles.cardName}>{set.shortName}</div>
        <div style={styles.cardBottom}>
          <div style={styles.progressTrack} />
          <div style={styles.progressLabel}>
            <span style={styles.progressMuted}>{set.totalCards} CARDS</span>
            <span style={{ ...styles.progressPct, color: accent }}>{tagline} →</span>
          </div>
        </div>
      </div>
    );
  };

  const renderFirstSetPicker = () => (
    <div style={styles.emptyState}>
      <h2 style={styles.emptyHeadline}>Pick the set you have the most of.</h2>
      <p style={styles.emptySubline}>
        Mark <span style={styles.emptySublineAccent}>five cards</span>. That&rsquo;s all it takes to start.
      </p>
      <div style={styles.emptyGrid}>
        {FIRST_SET_PICKS.map(pick => {
          const set = setList.find(s => s.id === pick.id);
          return set ? renderFirstPickCard(set, pick.tagline) : null;
        })}
      </div>
      <button style={styles.browseAllBtn} onClick={() => setBrowseAll(true)}>
        BROWSE ALL {setList.length} SETS ▾
      </button>
    </div>
  );

  const query = searchQuery.trim();
  const filtering = query !== '' || yearFilter !== 'all' || typeFilter !== 'all';

  const years = useMemo(() =>
    [...new Set(setList.map(s => s.year))].sort((a, b) => b - a), [setList]);

  // Set-level search: matches set name, year, brand and product-line label so
  // typing "2024 galaxy" or "sapphire" finds sets directly (the old search
  // only matched cards — a set title returned nothing).
  const setMatches = useMemo(() => {
    if (!filtering) return [];
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    return setList.filter(set => {
      const sec = SECTIONS.find(sc => sc.match(set));
      const hay = `${set.shortName} ${set.year} ${set.brand} ${sec ? sec.label : ''}`.toLowerCase();
      const okQ = tokens.every(t => hay.includes(t));
      const okY = yearFilter === 'all' || String(set.year) === String(yearFilter);
      const okT = typeFilter === 'all' || setType(set) === typeFilter;
      return okQ && okY && okT;
    }).sort(byNewest);
  }, [filtering, query, yearFilter, typeFilter, setList]);

  // Sets with progress, for the "Continue collecting" strip. Sorted closest-
  // to-complete first; capped so the strip stays a strip.
  const inProgress = useMemo(() => {
    return setList
      .filter(s => {
        const st = stats[s.id];
        return st && st.collected > 0 && st.collected < st.total;
      })
      .sort((a, b) => {
        const pa = stats[a.id].collected / (stats[a.id].total || 1);
        const pb = stats[b.id].collected / (stats[b.id].total || 1);
        return pb - pa;
      })
      .slice(0, 6);
  }, [setList, stats]);

  const renderSetCard = (set) => {
    const s = stats[set.id] || { collected: 0, total: set.totalCards };
    const pct = s.total ? Math.round((s.collected / s.total) * 100) : 0;
    const accent = lightAccent(set.color);
    const starred = starredSets.includes(set.id);
    return (
      <div
        key={set.id}
        style={{ ...styles.card, borderLeft: '3px solid ' + accent }}
        onClick={() => openSet(set.id)}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = accent;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 8px 26px ${accent}22`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.borderLeftColor = accent;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={styles.cardMeta}>
          <span>{set.year}</span>
          <span style={styles.cardMetaDot}>·</span>
          <span>{(set.brand || '').toUpperCase()}</span>
          {isNewSet(set) && <span style={styles.newBadge}>NEW</span>}
          <span style={{ flex: 1 }} />
          <button
            onClick={(e) => toggleStar(set.id, e)}
            aria-pressed={starred}
            aria-label={(starred ? 'Unstar ' : 'Star ') + set.shortName}
            title={starred ? 'Remove star' : 'Star this set'}
            style={{ ...styles.starBtn, color: starred ? 'var(--gold)' : 'var(--muted-9)' }}
          >&#9733;</button>
        </div>
        <div style={styles.cardName}>{set.shortName}</div>
        <div style={styles.cardDesc}>{set.description}</div>
        <div style={styles.cardBottom}>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${pct}%`, background: accent }} />
          </div>
          <div style={styles.progressLabel}>
            <span style={{ color: 'var(--text)' }}>{s.collected}</span>
            <span style={styles.progressMuted}>&nbsp;/ {s.total} collected</span>
            <span style={{ ...styles.progressPct, color: pct === 100 ? 'var(--gold)' : 'var(--muted)' }}>{pct}%</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSectionHeader = (section, sets, isOpen, gold) => {
    // section is null for the starred pseudo-section (gold=true)
    const accent = gold ? 'var(--gold)' : lightAccent(section.accent);
    const yrs = [...new Set(sets.map(s => s.year))];
    const range = yrs.length > 1 ? `${Math.min(...yrs)}–${Math.max(...yrs)}` : `${yrs[0] || ''}`;
    const hasNew = sets.some(isNewSet);
    return (
      <button
        style={styles.accordionHeader}
        aria-expanded={isOpen}
        onClick={() => toggleSection(gold ? 'starred' : section.id)}
      >
        <div style={{ ...styles.accordionAccent, background: gold ? 'var(--gold)' : accent }} />
        <span style={{ ...styles.accordionLabel, color: gold ? 'var(--gold)' : 'var(--text-strong)' }}>
          {gold ? <>&#9733; STARRED SETS</> : section.label}
        </span>
        <span style={styles.accordionPreview}>
          <span style={styles.accordionCount}>{sets.length} SET{sets.length !== 1 ? 'S' : ''}</span>
          {range && <span style={styles.yearChip}>{range}</span>}
          {hasNew && <span style={styles.newBadge}>NEW</span>}
        </span>
        <div style={{ ...styles.accordionChevron, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          ▾
        </div>
      </button>
    );
  };

  const foundCardCount = cardResults ? cardResults.reduce((a, r) => a + r.cards.length, 0) : 0;

  return (
    <div style={styles.page}>
      <NavDrawer user={user} />
      <div style={styles.stars} />

      <header style={styles.header}>
        <div style={styles.headerInner}><img src='/logo.png' style={{width:'52px',height:'52px',borderRadius:'8px',marginRight:'4px'}} alt='CollectionKeeper' />
          <div style={styles.logoLine}>COLLECTION KEEPER</div>
          <h1 style={styles.title}>MY <span style={styles.titleAccent}>CHECKLISTS</span></h1>
          <div style={styles.subtitle}>// TRACK · SHARE · TRADE //</div>
        </div>

      </header>

      <main style={styles.main}>
        {/* CK 3.0: a zero-card collector gets three sets and one sentence
            instead of the search + ~50-section accordion. Everything below
            renders exactly as before once they have a single row (or tap
            "Browse all", or the flag is off, or the count query fails). */}
        {showEmptyState ? renderFirstSetPicker() : (<>
        <div style={{position:'relative',marginBottom:'2px'}}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            placeholder='Find a set or card — try "2024" or "sapphire"'
            aria-label="Find a set or card"
            style={{width:'100%',background:'var(--panel)',border:'1px solid ' + (searchQuery ? 'var(--cyan)' : 'var(--border-2)'),color:'var(--text-strong)',fontFamily:"'Rajdhani', sans-serif",fontWeight:500,fontSize:'17px',padding:'12px 46px 12px 14px',borderRadius:'5px',outline:'none',boxSizing:'border-box',letterSpacing:'0.3px'}} />
          {searchQuery ? <span onClick={() => {setSearchQuery('');setCardResults(null);}} style={{position:'absolute',right:'0',top:'0',bottom:'0',width:'44px',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'var(--text)',fontSize:'18px'}}>✕</span> : <span style={{position:'absolute',right:'0',top:'0',bottom:'0',width:'44px',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',fontSize:'18px'}}>⌕</span>}
        </div>

        <div style={{ ...styles.chips, marginBottom: '8px' }} role="group" aria-label="Filter sets by year">
          {['all', ...years].map(y => {
            const active = String(yearFilter) === String(y);
            return (
              <button
                key={y}
                aria-pressed={active}
                onClick={() => setYearFilter(active && y !== 'all' ? 'all' : y)}
                style={{ ...styles.chip, ...(active ? styles.chipActive : {}) }}
              >{y === 'all' ? 'ALL YEARS' : y}</button>
            );
          })}
        </div>

        <div style={styles.chips} role="group" aria-label="Filter sets by product type">
          {['all', ...SET_TYPES].map(t => {
            const active = typeFilter === t;
            return (
              <button
                key={t}
                aria-pressed={active}
                onClick={() => setTypeFilter(active && t !== 'all' ? 'all' : t)}
                style={{ ...styles.chip, ...(active ? styles.chipActive : {}) }}
              >{t === 'all' ? 'ALL TYPES' : t.toUpperCase()}</button>
            );
          })}
        </div>

        {filtering && (
          <div style={{display:'flex',flexDirection:'column',gap:'8px',overflowX:'hidden',width:'100%',boxSizing:'border-box'}}>
            <div style={styles.resultCount}>
              {setMatches.length} SET{setMatches.length !== 1 ? 'S' : ''}{yearFilter !== 'all' ? ` · ${yearFilter}` : ''}{typeFilter !== 'all' ? ` · ${typeFilter.toUpperCase()}` : ''}{query ? ` MATCHING "${query.toUpperCase()}"` : ''}
            </div>
            {setMatches.length > 0 && (
              <div style={styles.grid}>
                {setMatches.map(renderSetCard)}
              </div>
            )}
            {query && cardResults !== null && (
              <>
                <div style={{ ...styles.resultCount, marginTop: '14px' }}>{foundCardCount} CARD{foundCardCount !== 1 ? 'S' : ''}</div>
                {cardResults.length === 0 && setMatches.length === 0 && (
                  <div style={{fontFamily:"'Rajdhani', sans-serif",fontSize:'14px',color:'var(--muted)',textAlign:'center',padding:'40px 0'}}>No sets or cards match your search.</div>
                )}
                {cardResults.map((result, i) => (
                  <div key={i} style={{background:'var(--panel-deep)',border:'1px solid var(--border)',borderRadius:'4px',overflow:'hidden',width:'100%',boxSizing:'border-box'}}>
                    <div style={{padding:'8px 12px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer',overflow:'hidden'}} onClick={() => openSet(result.set.id)}>
                      <div style={{minWidth:0,flex:1,overflow:'hidden'}}>
                        <div style={{fontFamily:"'Orbitron', monospace",fontSize:'9px',letterSpacing:'2px',color:lightAccent(result.set.color),whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{result.set.shortName}</div>
                        <div style={{fontFamily:"'Share Tech Mono', monospace",fontSize:'10px',color:'var(--muted)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{result.subSet.title}</div>
                      </div>
                      <div style={{fontFamily:"'Orbitron', monospace",fontSize:'9px',color:'var(--muted)',flexShrink:0,marginLeft:'8px'}}>{result.cards.length} -&gt;</div>
                    </div>
                    {result.cards.slice(0,4).map(card => (
                      <div key={card.n} style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',borderBottom:'1px solid var(--panel-3)',cursor:'pointer',overflow:'hidden'}} onClick={() => openSet(result.set.id)}>
                        <div style={{fontFamily:"'Share Tech Mono', monospace",fontSize:'10px',color:'var(--muted)',flexShrink:0,minWidth:'28px'}}>{card.n}</div>
                        <div style={{flex:1,minWidth:0,overflow:'hidden'}}>
                          <div style={{fontFamily:"'Rajdhani', sans-serif",fontSize:'13px',fontWeight:600,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{card.name}</div>
                          {card.artist && <div style={{fontFamily:"'Share Tech Mono', monospace",fontSize:'10px',color:'var(--muted)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{card.artist}</div>}
                        </div>
                      </div>
                    ))}
                    {result.cards.length > 4 && <div style={{fontFamily:"'Share Tech Mono', monospace",fontSize:'10px',color:'var(--muted)',padding:'7px 12px',cursor:'pointer'}} onClick={() => openSet(result.set.id)}>+ {result.cards.length - 4} more</div>}
                  </div>
                ))}
              </>
            )}
            {!query && setMatches.length === 0 && (
              <div style={{fontFamily:"'Rajdhani', sans-serif",fontSize:'14px',color:'var(--muted)',textAlign:'center',padding:'40px 0'}}>No sets from {yearFilter}.</div>
            )}
          </div>
        )}

        {!filtering && <>

        {!loading && inProgress.length > 0 && (
          <>
            <div style={styles.blockLabel}>CONTINUE COLLECTING<span style={styles.blockLabelSub}>Sets you have in progress</span></div>
            <div style={{ ...styles.grid, marginBottom: '14px' }}>
              {inProgress.map(renderSetCard)}
            </div>
          </>
        )}

        {starredSets.length > 0 && (
          <div style={{ ...styles.accordion, border: '1px solid rgba(var(--gold-rgb),0.35)' }}>
            {renderSectionHeader(null, setList.filter(s => starredSets.includes(s.id)), openSections.starred, true)}
            {openSections.starred && (
              <div style={styles.accordionBody}>
                <div style={styles.grid}>
                  {setList.filter(s => starredSets.includes(s.id)).map(renderSetCard)}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={styles.blockLabel}>ALL SETS<span style={styles.blockLabelSub}>By product line</span></div>

        {SECTIONS.map(section => {
          const sets = setList
            .filter(section.match)
            .sort(byNewest);

          if (sets.length === 0) return null;
          const isOpen = openSections[section.id];

          return (
            <div key={section.id} style={styles.accordion}>
              {renderSectionHeader(section, sets, isOpen, false)}
              {isOpen && (
                <div style={styles.accordionBody}>
                  <div style={styles.grid}>
                    {sets.map(renderSetCard)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </>
        }
        </>)}
      </main>

      <footer style={styles.footer}>
        <div style={{ ...styles.footerText, color: 'var(--text)' }}>© 2026 COLLECTIONKEEPER.ORG</div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <a href="/deployments" style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted)', textDecoration: 'none' }}>Deployments</a>
          <a href="/terms" style={{ fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted)', textDecoration: 'none' }}>Terms & Privacy</a>
        </div>
      </footer>

      <FeedbackWidget user={user} profile={profile} />
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    color: 'var(--text)',
    position: 'relative',
    overflowX: 'hidden',
  },
  stars: {
    position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)', zIndex: 0, pointerEvents: 'none',
    background: 'radial-gradient(ellipse at 20% 50%, rgba(0,100,200,0.04) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(200,150,0,0.04) 0%, transparent 60%)',
  },
  header: {
    position: 'relative', zIndex: 10,
    borderBottom: '1px solid rgba(var(--wash-rgb),0.06)',
    padding: '32px 20px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    flexWrap: 'wrap', gap: '16px',
  },
  headerInner: { display: 'flex', flexDirection: 'column', gap: '4px' },
  logoLine: {
    fontFamily: "'Orbitron', monospace", fontSize: '9px',
    letterSpacing: '6px', color: 'var(--brand-gold)', opacity: 0.8,
  },
  title: {
    fontFamily: "'Orbitron', monospace", fontSize: 'clamp(18px, 3vw, 28px)',
    fontWeight: 900, letterSpacing: '2px', color: 'var(--text-strong)', margin: 0,
  },
  titleAccent: { color: 'var(--brand-gold)' },
  subtitle: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '10px',
    letterSpacing: '3px', color: 'var(--muted)',
  },
  main: {
    position: 'relative', zIndex: 1,
    maxWidth: '1100px', margin: '0 auto',
    padding: '40px 24px 80px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },

  // Year filter chips
  chips: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '18px' },
  chip: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', letterSpacing: '1px',
    color: 'var(--muted)', background: 'var(--panel)',
    border: '1px solid var(--border)', borderRadius: '15px',
    padding: '5px 14px', cursor: 'pointer', transition: 'all 0.15s',
  },
  chipActive: {
    color: 'var(--bg)', background: 'var(--cyan)', border: '1px solid var(--cyan)',
  },

  // Block labels (Continue collecting / All sets)
  blockLabel: {
    fontFamily: "'Orbitron', monospace", fontSize: '10px', fontWeight: 700,
    letterSpacing: '4px', color: 'var(--muted)',
    margin: '14px 0 2px',
  },
  blockLabelSub: {
    fontFamily: "'Rajdhani', sans-serif", fontWeight: 500, fontSize: '13px',
    letterSpacing: '0.2px', color: 'var(--muted-9)', textTransform: 'none',
    marginLeft: '10px',
  },
  resultCount: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '11px',
    letterSpacing: '2px', color: 'var(--cyan)', marginBottom: '4px',
  },

  // Accordion
  accordion: {
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid var(--border)',
    background: 'rgba(var(--wash-rgb),0.012)',
  },
  accordionHeader: {
    width: '100%', background: 'transparent',
    border: 'none',
    padding: '14px 18px',
    display: 'flex', alignItems: 'center', gap: '12px',
    cursor: 'pointer', textAlign: 'left',
    transition: 'background 0.15s',
  },
  accordionAccent: { width: '3px', height: '18px', borderRadius: '2px', flexShrink: 0 },
  accordionLabel: {
    fontFamily: "'Orbitron', monospace", fontSize: '12px',
    fontWeight: 700, letterSpacing: '2.5px',
  },
  accordionPreview: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginLeft: '2px',
  },
  accordionCount: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '11px',
    color: 'var(--muted)', letterSpacing: '1px',
  },
  yearChip: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '10.5px', letterSpacing: '0.5px',
    color: 'var(--muted)', background: 'var(--panel)',
    border: '1px solid var(--border)', borderRadius: '3px', padding: '1px 7px',
  },
  accordionChevron: {
    fontFamily: "'Share Tech Mono', monospace",
    color: 'var(--muted)', fontSize: '15px',
    transition: 'transform 0.2s ease',
    lineHeight: 1, marginLeft: 'auto', flexShrink: 0,
  },
  accordionBody: {
    padding: '10px', borderTop: '1px solid var(--border)',
  },

  // Set grid
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
    gap: '10px',
  },

  // CK 3.0 first-set empty state
  emptyState: { padding: '18px 0 6px' },
  emptyHeadline: {
    fontFamily: "'Orbitron', monospace", fontSize: '19px', fontWeight: 800,
    color: 'var(--text-strong)', letterSpacing: '0.5px', lineHeight: 1.35,
    margin: '0 0 8px', textWrap: 'balance',
  },
  emptySubline: {
    fontFamily: "'Rajdhani', sans-serif", fontWeight: 500, fontSize: '16px',
    color: 'var(--muted)', margin: '0 0 20px',
  },
  emptySublineAccent: { color: 'var(--cyan)', fontWeight: 700 },
  emptyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
    gap: '12px',
    marginBottom: '14px',
  },
  browseAllBtn: {
    display: 'block', width: '100%', boxSizing: 'border-box',
    background: 'transparent', border: '1px dashed var(--border-2)',
    borderRadius: '4px', padding: '17px 10px', cursor: 'pointer',
    fontFamily: "'Orbitron', monospace", fontSize: '10px', fontWeight: 600,
    letterSpacing: '2px', color: 'var(--muted)', textAlign: 'center',
  },
  card: {
    background: 'var(--panel)',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    padding: '14px 16px 13px',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
    display: 'flex', flexDirection: 'column', gap: '5px',
  },
  cardMeta: {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontFamily: "'Share Tech Mono', monospace", fontSize: '11px',
    letterSpacing: '1px', color: 'var(--muted)',
  },
  cardMetaDot: { color: 'var(--muted-9)' },
  newBadge: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '10px',
    letterSpacing: '1.5px',
    color: 'var(--bg)', background: 'var(--gold)',
    padding: '1.5px 7px', borderRadius: '3px',
  },
  starBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '17px', lineHeight: 1, padding: '10px', margin: '-10px',
    transition: 'color 0.15s', flexShrink: 0,
  },
  cardName: {
    fontFamily: "'Rajdhani', sans-serif", fontSize: '17px',
    fontWeight: 600, color: 'var(--text-strong)', letterSpacing: '0.2px',
    lineHeight: 1.25,
  },
  cardDesc: {
    fontFamily: "'Rajdhani', sans-serif", fontWeight: 500, fontSize: '13.5px',
    color: 'var(--muted)', lineHeight: 1.45, flex: 1,
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },
  cardBottom: { marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '5px' },
  progressTrack: {
    height: '3px', background: 'rgba(var(--wash-rgb),0.08)', borderRadius: '2px', overflow: 'hidden',
  },
  progressFill: {
    height: '100%', borderRadius: '2px', transition: 'width 0.4s ease',
  },
  progressLabel: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '11px',
    display: 'flex', alignItems: 'baseline',
  },
  progressMuted: { color: 'var(--muted-9)' },
  progressPct: { marginLeft: 'auto', fontSize: '11px', fontWeight: 700 },

  footer: {
    borderTop: '1px solid rgba(var(--wash-rgb),0.04)',
    padding: '20px 20px', marginTop: '40px',
  },
  footerText: {
    fontFamily: "'Orbitron', monospace", fontSize: '8px',
    letterSpacing: '3px', color: 'var(--border)', textAlign: 'center',
  },
};
